/**
 * COU progress regions — `data-ms-progress`.
 *
 * Mark a region with `data-ms-progress`. Leave it empty and the SDK draws the
 * dashboard's own bar into it. Put ONE offer's markup inside it carrying
 * `data-ms-state`, and the SDK repeats that markup once per offer — resolving its
 * macros against *that* offer — and fills `data-ms-state` in with `done` /
 * `current` / `todo`.
 *
 * So `<img data-ms-state src="{{image}}">` is an offer-image strip and
 * `<span data-ms-state>&#9733;</span>` is a star strip. A region that holds markup
 * carrying no `data-ms-state` is left alone: it only gets `--ms-progress` and the
 * count attributes published on it, which is enough for a continuous fill.
 *
 * Mode is decided from the TEMPLATE, never from the live DOM: once the first pass
 * has run the region holds the SDK's own output, so re-reading it would misclassify.
 */
import { render, h } from 'preact';
import { ProgressBar } from '../components/ProgressBar';
export const COU_PROGRESS_ATTRIBUTE = 'data-ms-progress';
/** Marks the repeated unit AND is the CSS hook the SDK writes into. One name, both jobs. */
export const COU_PROGRESS_STATE_ATTRIBUTE = 'data-ms-state';
const REGION_SELECTOR = `[${COU_PROGRESS_ATTRIBUTE}]`;
const UNIT_SELECTOR = `[${COU_PROGRESS_STATE_ATTRIBUTE}]`;
/**
 * Same fallback `ProgressBar` uses for the stepper, so a recipe written against
 * `var(--ms-progress-color)` can never resolve to nothing and paint an invisible bar
 * on a theme whose colour picker was left empty.
 */
const DEFAULT_PROGRESS_COLOR = '#007bff';
/**
 * The one rule that decides what a region is, applied to a region as the publisher
 * wrote it. `planProgressRegions` and the morph skip both go through here so the two
 * cannot disagree about who owns a region's children.
 */
function progressRegionMode(region) {
    if (region.querySelector(UNIT_SELECTOR)) {
        return 'repeat';
    }
    /** Empty means empty: the SDK never replaces content a publisher wrote. */
    return region.children.length === 0 && !region.textContent?.trim()
        ? 'auto'
        : 'state';
}
/**
 * Reads the publisher's template (post `expandCouDescriptionMacrosToRawHtml`, pre
 * macro resolution) and decides what each `[data-ms-progress]` region is, in document
 * order — the same order `applyProgressRegions` walks the live DOM in.
 */
export function planProgressRegions(doc, expandedTemplate) {
    const holder = doc.createElement('template');
    holder.innerHTML = expandedTemplate;
    const plans = [];
    holder.content.querySelectorAll(REGION_SELECTOR).forEach(region => {
        const unit = region.querySelector(UNIT_SELECTOR);
        plans.push({
            mode: progressRegionMode(region),
            unit: unit ? unit.outerHTML : null,
        });
    });
    return plans;
}
/**
 * Regions whose children the SDK writes rather than the template.
 *
 * A WeakSet rather than an attribute on purpose: `morphInnerHTML` syncs a region's
 * attributes against the template before it looks at its children, so any marker
 * attribute the SDK wrote would already be gone by the time the skip is consulted.
 * Membership is per element, so two SDK instances never see each other's regions.
 *
 * That rules out `data-ms-preserve` too: the morph reads it after the same sync, so
 * only a template can set it. `rehydrateScripts` consults this set as well, so the
 * SDK's repeated copies of a unit never run the unit's `<script>` again.
 */
const sdkOwnedRegions = new WeakSet();
/** True when `morphInnerHTML` must leave this node's subtree alone. */
export function isSdkOwnedProgressRegion(node) {
    return node.nodeType === 1 && sdkOwnedRegions.has(node);
}
/** Regions currently driven by a mounted `ProgressBar`, so they unmount cleanly. */
const autoMounted = new WeakSet();
const painted = new WeakMap();
/**
 * A progress bar answers "how far through the deck am I", which is not a question a
 * single-offer deck has. `ProgressBar` has always returned null at one offer; a bar
 * the publisher drew gets the same rule.
 */
function hasProgressToShow(s) {
    return s.offers.length > 1;
}
/**
 * `progress_bar_type: 'none'` switches the dashboard's own bar off, so it reaches
 * only the regions the SDK draws. A bar the publisher drew is their markup, and a
 * dropdown describing the SDK's stepper does not get to delete it.
 */
function drawsNothing(mode, s) {
    return (mode === 'auto' && s.settings.progress_bar_type?.toLowerCase() === 'none');
}
function writeRegionState(region, s, mode) {
    const total = s.offers.length;
    region.setAttribute('data-ms-current', String(s.currentIndex + 1));
    region.setAttribute('data-ms-total', String(total));
    /** Completion, not position: offer 1 of 4 is 25% done, not an empty bar. */
    const fraction = total > 0 ? (s.currentIndex + 1) / total : 0;
    region.style.setProperty('--ms-progress', String(fraction));
    region.style.setProperty('--ms-progress-color', s.styles?.header?.background || DEFAULT_PROGRESS_COLOR);
    /**
     * The only dashboard Progress Bar setting that reaches a publisher-drawn bar: it is
     * about whether the bar is on screen at all, not about what it looks like. Written
     * as an inline `display` the same way `applyOfferNavVisibility` hides a dead-end
     * arrow, and cleared to `''` rather than a value so the publisher's own `display`
     * applies whenever the bar is shown.
     */
    const hide = !hasProgressToShow(s) ||
        drawsNothing(mode, s) ||
        (s.isMobile && !!s.settings.hide_progress_bar_mobile);
    region.style.display = hide ? 'none' : '';
}
/**
 * N copies of one element: any `id` inside it would become N duplicate ids in one
 * shadow root, and a duplicate id also makes `morphInnerHTML`'s keyed matching
 * ambiguous. The publisher's own hook into a unit is `[data-ms-state]`, which every
 * recipe uses and which survives.
 */
function stripIds(el) {
    el.removeAttribute('id');
    el.querySelectorAll('[id]').forEach(n => n.removeAttribute('id'));
}
/** Removes the copies this module painted. Never touches markup a publisher wrote. */
function clearUnits(region) {
    if (!painted.has(region)) {
        return;
    }
    region
        .querySelectorAll(UNIT_SELECTOR)
        .forEach(el => el.remove());
    painted.delete(region);
}
function unmountAuto(region) {
    if (!autoMounted.has(region)) {
        return;
    }
    render(null, region);
    autoMounted.delete(region);
}
/**
 * Hands a region's children back to the template: drops the morph skip, unmounts an
 * auto bar, and forgets what was painted so `clearUnits` can never reach markup the
 * publisher wrote.
 */
function releaseRegion(region) {
    unmountAuto(region);
    painted.delete(region);
    sdkOwnedRegions.delete(region);
}
/**
 * Run immediately before every `morphInnerHTML`, against the plan being morphed IN.
 *
 * Ownership is what makes the morph skip a region's children, so it has to describe
 * the incoming template, not the one that painted the DOM. A region the SDK drew and
 * still draws keeps the skip — that is the ordinary offer step, where reconciling the
 * repeats against the template's single unit would delete all but the first. A region
 * that stops being SDK-drawn (the template changed, or a `customLayout.render` took
 * over and there is no plan at all) is released here so the morph can reconcile it;
 * otherwise the skip outlives the plan and the publisher's new markup is locked out
 * behind stale SDK children.
 *
 * Ownership is only ever dropped here, never taken: a region the SDK is about to start
 * drawing must be morphed normally first, so the template's own content — including the
 * single unit `syncUnits` then repeats — lands before `applyProgressRegions` claims it.
 * Pass `null` for a source with no plan to release every region.
 */
export function releaseProgressRegions(root, plans) {
    root.querySelectorAll(REGION_SELECTOR).forEach((region, i) => {
        if (!sdkOwnedRegions.has(region)) {
            return;
        }
        const plan = plans?.[i];
        if (plan && plan.mode !== 'state') {
            return;
        }
        releaseRegion(region);
    });
}
/**
 * A tile the publisher already made interactive — a button, a link, a form control, a
 * `data-ms-action` at or inside it — keeps its own semantics. `progressJumpTarget`
 * declines to jump for the `data-ms-action` case for the same reason, so the two agree
 * about which tiles the SDK's own navigation owns.
 */
const PUBLISHER_INTERACTIVE_SELECTOR = 'a[href], button, input, select, textarea, [data-ms-action]';
function isPublisherInteractive(unit) {
    return (unit.matches(PUBLISHER_INTERACTIVE_SELECTOR) ||
        unit.querySelector('[data-ms-action]') != null);
}
/**
 * Keyboard parity for the jump the repeat mode just made possible: the SDK turned a
 * tile into navigation, so the SDK owes it a tab stop, a name, and Enter/Space.
 *
 * `pointer-events: none` is the documented opt-out for the click, so it opts out of the
 * keyboard too — a tile nobody can click must not collect a tab stop that does nothing.
 * One `getComputedStyle` per region per pass: the opt-out is written against the unit
 * selector, so it is the same verdict for every copy.
 */
function markUnitAffordances(region, units, interactive, s) {
    const first = units[0];
    const clickable = !interactive &&
        first != null &&
        region.ownerDocument.defaultView?.getComputedStyle(first).pointerEvents !==
            'none';
    units.forEach((el, i) => {
        if (i === s.currentIndex) {
            el.setAttribute('aria-current', 'step');
        }
        else {
            el.removeAttribute('aria-current');
        }
        if (interactive) {
            return;
        }
        if (!clickable) {
            el.removeAttribute('role');
            el.removeAttribute('tabindex');
            el.removeAttribute('aria-label');
            return;
        }
        el.setAttribute('role', 'button');
        el.setAttribute('tabindex', '0');
        el.setAttribute('aria-label', `Offer ${i + 1} of ${s.offers.length}`);
    });
}
function syncUnits(region, unit, s) {
    const existing = Array.from(region.querySelectorAll(UNIT_SELECTOR));
    const prev = painted.get(region);
    /**
     * Repaint only when the resolved set actually changed. Stepping an offer must not
     * touch the DOM beyond `data-ms-state`, or every per-unit CSS transition restarts
     * from scratch and nothing the publisher wrote animates.
     */
    const reusable = prev != null &&
        prev.unit === unit &&
        prev.offers === s.offers &&
        prev.count === existing.length;
    if (!reusable) {
        const anchor = existing[0] ?? null;
        const parent = (anchor?.parentElement ?? region);
        /** Holds the publisher's chosen position, so markup around the unit keeps its place. */
        const slot = region.ownerDocument.createComment('ms-progress');
        parent.insertBefore(slot, anchor);
        existing.forEach(el => el.remove());
        const holder = region.ownerDocument.createElement('template');
        holder.innerHTML = s.offers
            .map(offer => s.resolveUnit(unit, offer))
            .join('');
        const built = Array.from(holder.content.children);
        const interactive = built.some(isPublisherInteractive);
        built.forEach(child => {
            stripIds(child);
            parent.insertBefore(child, slot);
        });
        slot.remove();
        painted.set(region, {
            unit,
            offers: s.offers,
            count: s.offers.length,
            interactive,
        });
    }
    const units = Array.from(region.querySelectorAll(UNIT_SELECTOR));
    units.forEach((el, i) => {
        const state = i < s.currentIndex ? 'done' : i === s.currentIndex ? 'current' : 'todo';
        if (el.getAttribute(COU_PROGRESS_STATE_ATTRIBUTE) !== state) {
            el.setAttribute(COU_PROGRESS_STATE_ATTRIBUTE, state);
        }
    });
    markUnitAffordances(region, units, painted.get(region)?.interactive ?? false, s);
}
function syncAuto(region, s) {
    if (!s.styles) {
        return;
    }
    autoMounted.add(region);
    render(h(ProgressBar, {
        settings: s.settings,
        styles: s.styles,
        currentIndex: s.currentIndex,
        totalOffers: s.offers.length,
        isMobileLayout: s.isMobile,
        onOfferSelect: s.onJump,
    }), region);
}
/**
 * Drive every `[data-ms-progress]` in `root`. Runs after every `morphInnerHTML`, like
 * `applyOfferNavVisibility`: the morph syncs a region's attributes against the
 * template, which strips the counts and the inline custom properties written here.
 */
export function applyProgressRegions(root, s) {
    root.querySelectorAll(REGION_SELECTOR).forEach((region, i) => {
        const plan = s.plans[i] ?? null;
        writeRegionState(region, s, plan?.mode ?? null);
        if (!plan) {
            return;
        }
        const owned = plan.mode !== 'state';
        if (owned) {
            sdkOwnedRegions.add(region);
        }
        else {
            sdkOwnedRegions.delete(region);
        }
        /** Nothing to draw at one offer, and nothing left over from when there were more. */
        if (!hasProgressToShow(s) || drawsNothing(plan.mode, s)) {
            unmountAuto(region);
            clearUnits(region);
            return;
        }
        if (plan.mode === 'auto') {
            clearUnits(region);
            syncAuto(region, s);
            return;
        }
        unmountAuto(region);
        if (plan.mode === 'repeat' && plan.unit) {
            syncUnits(region, plan.unit, s);
            return;
        }
        clearUnits(region);
    });
}
/** Unmount Preact-driven regions so their vdom does not outlive the COU root. */
export function teardownProgressRegions(root) {
    root.querySelectorAll(REGION_SELECTOR).forEach(releaseRegion);
}
/**
 * Resolves a click inside a progress region to the offer index it targets. A unit
 * shows an offer, so clicking it goes to that offer — no opt-in attribute. A theme
 * that wants a strip which only looks writes `pointer-events: none`.
 *
 * A `data-ms-action` at or below the unit still wins: a publisher who put an action
 * on the tile meant it, and the closer hook is the more specific one.
 */
export function progressJumpTarget(target) {
    const unit = target?.closest(UNIT_SELECTOR);
    if (!unit) {
        return null;
    }
    const region = unit.closest(REGION_SELECTOR);
    if (!region) {
        return null;
    }
    const action = target?.closest('[data-ms-action]');
    if (action && (action === unit || unit.contains(action))) {
        return null;
    }
    const units = Array.from(region.querySelectorAll(UNIT_SELECTOR));
    const index = units.indexOf(unit);
    return index >= 0 ? index : null;
}
/**
 * Keyboard twin of `progressJumpTarget`. The SDK makes the unit *itself* focusable, so
 * only the unit answers to Enter/Space — anything focusable a publisher put inside a
 * tile keeps its own keyboard behaviour instead of being swallowed by the jump.
 */
export function progressKeyJumpTarget(target) {
    return target?.matches(UNIT_SELECTOR) ? progressJumpTarget(target) : null;
}
/**
 * Default styling for a progress region, injected into the shadow root ahead of the
 * publisher's COU CSS.
 *
 * Every selector is wrapped in `:where()`, which forces specificity to **zero**. A
 * publisher's own rule therefore always wins — including a bare `[data-ms-state] {}` —
 * with no `!important` and nothing to learn about overriding. The point is that a
 * pasted recipe is the values you want to change and nothing else.
 *
 * `counter-reset` / `counter-increment` are here so a numbered strip is one readable
 * line in the publisher's CSS (`content: counter(ms-offer)`) instead of three obscure
 * ones. A counter is invisible until something references it, so this costs nothing.
 *
 * Layout and motion only. The single appearance default is `opacity` on `todo`,
 * because "not reached = faded" reads correctly for an image, a dot, a star, a number
 * and a segment alike; anything more opinionated would silently restyle a `todo` state
 * the publisher coloured on purpose.
 */
export const COU_PROGRESS_DEFAULT_CSS = `
:where([data-ms-progress]) {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 8px;
  counter-reset: ms-offer;
}
:where([data-ms-progress] [data-ms-state]) {
  flex: 0 0 auto;
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  counter-increment: ms-offer;
  cursor: pointer;
  transition: opacity .25s ease, filter .25s ease, transform .25s ease,
              background-color .25s ease, color .25s ease, border-color .25s ease;
}
:where([data-ms-progress] [data-ms-state='todo']) {
  opacity: .45;
}
`;
